import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import styled from "styled-components";

import { HeroControls } from "./hero-controls";
import { imgSrc } from "../../../helpers/static";
import { t, tp } from "../../../constant/translations";
import { updateBlock } from "../../../redux/modules/blocks";

const DEFAULT_HERO = imgSrc("block-hero-default.jpg");

const HeroSection = styled.section`
  position: relative;
  height: ${({ $tall }) => ($tall ? "420px" : "320px")};
  background: #2a2f36;
  flex-shrink: 0;
  transition: height 0.25s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 6px 18px -8px rgba(0, 0, 0, 0.35);
`;

const Stage = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
`;

const Slide = styled.div`
  position: absolute;
  inset: 0;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  pointer-events: ${({ $active }) => ($active ? "auto" : "none")};
  transition: opacity 0.4s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    ${({ $muted }) =>
      $muted && `filter: grayscale(0.25) brightness(1.05) contrast(0.95);`}
  }
`;

const Shade = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 2;
  background: linear-gradient(
    180deg,
    transparent 0%,
    transparent 60%,
    rgba(0, 0, 0, 0.55) 100%
  );
`;

const Dots = styled.div`
  position: absolute;
  left: 28px;
  top: 22px;
  z-index: 5;
  display: flex;
  gap: 5px;
`;

const Dot = styled.button`
  width: 22px;
  height: ${({ $active }) => ($active ? "3px" : "2px")};
  background: ${({ $active }) =>
    $active ? "#fff" : "rgba(255, 255, 255, 0.35)"};
  border: none;
  padding: 0;
  cursor: pointer;
  transition: background 0.15s, height 0.15s;

  &:hover {
    background: rgba(255, 255, 255, 0.7);
  }
`;

const Arrows = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
  pointer-events: none;
  z-index: 4;
`;

const Arrow = styled.button`
  pointer-events: auto;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s, background 0.15s;
  backdrop-filter: blur(6px);

  ${HeroSection}:hover & {
    opacity: 1;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.65);
  }
`;

const Identity = styled.div`
  position: absolute;
  left: 32px;
  right: 32px;
  bottom: 26px;
  z-index: 4;
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 720px;
  pointer-events: none;
`;

const Title = styled.div`
  font-size: ${({ $scale }) => `${38 * $scale}px`};
  font-weight: 700;
  letter-spacing: -0.6px;
  line-height: 1;
  text-shadow: 0 2px 18px rgba(0, 0, 0, 0.45);
`;

const OverlayLogo = styled.img`
  height: ${({ $scale }) => `${42 * $scale}px`};
  width: auto;
  max-width: 360px;
  object-fit: contain;
  align-self: flex-start;
  opacity: ${({ $invert }) => ($invert ? 0.85 : 0.92)};
  filter: ${({ $invert }) =>
    $invert
      ? "brightness(0) invert(1) drop-shadow(0 2px 18px rgba(0, 0, 0, 0.55))"
      : "drop-shadow(0 2px 18px rgba(0, 0, 0, 0.55))"};
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${({ $scale }) => `${10 * $scale}px ${16 * $scale}px`};
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(8px);
  border-radius: 4px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  align-self: flex-start;

  img {
    height: ${({ $scale }) => `${28 * $scale}px`};
    width: auto;
    max-width: 320px;
    object-fit: contain;
  }
`;

const AddressLine = styled.div`
  font-size: 13.5px;
  color: rgba(255, 255, 255, 0.92);
  font-weight: 500;
  letter-spacing: 0.1px;
  text-shadow: 0 1px 8px rgba(0, 0, 0, 0.5);

  .dot {
    display: inline-block;
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.5);
    margin: 0 10px;
    vertical-align: middle;
    position: relative;
    top: -2px;
  }

  .mgr {
    color: rgba(255, 255, 255, 0.7);
    font-weight: 400;
  }

  .mgr b,
  > b {
    color: #fff;
    font-weight: 600;
  }
`;

const SmartImg = ({ file, alt, className }) => {
  const sources = [file.optimized_url, file.url].filter(Boolean);
  const [idx, setIdx] = React.useState(0);
  const src = sources[idx] || sources[0];
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => {
        if (idx < sources.length - 1) setIdx(idx + 1);
      }}
    />
  );
};

const Brand = ({ mode, logo, scale, invert, name }) => {
  const logoSrc = logo && (logo.optimized_url || logo.url);
  if (mode === "overlay" && logoSrc) {
    return (
      <OverlayLogo src={logoSrc} alt={name} $scale={scale} $invert={invert} />
    );
  }
  if (mode === "chip" && logoSrc) {
    return (
      <Chip $scale={scale}>
        <img src={logoSrc} alt={name} />
      </Chip>
    );
  }
  if (mode === "text") {
    return <Title $scale={scale}>{name}</Title>;
  }
  return null;
};

const buildInitialState = (block) => {
  const hero = (block && block.config && block.config.hero) || {};
  const images = (hero.images || []).filter((img) => img && img.url);
  const logo = hero.logo && hero.logo.url ? hero.logo : null;
  const primary =
    hero.primary && images.some((img) => img.uuid === hero.primary)
      ? hero.primary
      : null;
  return {
    images,
    primary,
    logo,
    logoMode: hero.logo_mode || "text",
    logoScale: typeof hero.logo_scale === "number" ? hero.logo_scale : 1,
    needsInvert: hero.needs_invert !== false,
  };
};

const HeroComponent = ({ block, updateBlock }) => {
  const initial = useMemo(() => buildInitialState(block), [block.id]);
  const initialActive = initial.primary
    ? Math.max(
        0,
        initial.images.findIndex((img) => img.uuid === initial.primary)
      )
    : 0;
  const [images, setImages] = useState(initial.images);
  const [primary, setPrimary] = useState(initial.primary);
  const [activeIdx, setActiveIdx] = useState(initialActive);
  const [logo, setLogo] = useState(initial.logo);
  const [logoMode, setLogoMode] = useState(initial.logoMode);
  const [logoScale, setLogoScale] = useState(initial.logoScale);
  const [needsInvert, setNeedsInvert] = useState(initial.needsInvert);
  const timerRef = useRef(null);
  const saveRef = useRef(null);
  const hydratedRef = useRef(false);
  const blockIdRef = useRef(block.id);

  useEffect(() => {
    if (blockIdRef.current !== block.id) {
      blockIdRef.current = block.id;
      hydratedRef.current = false;
      setImages(initial.images);
      setPrimary(initial.primary);
      setLogo(initial.logo);
      setLogoMode(initial.logoMode);
      setLogoScale(initial.logoScale);
      setNeedsInvert(initial.needsInvert);
      const nextActive = initial.primary
        ? Math.max(
            0,
            initial.images.findIndex((img) => img.uuid === initial.primary)
          )
        : 0;
      setActiveIdx(nextActive);
    }
  }, [block.id, initial]);

  useEffect(() => {
    if (!hydratedRef.current) {
      hydratedRef.current = true;
      return undefined;
    }
    clearTimeout(saveRef.current);
    saveRef.current = setTimeout(() => {
      const validPrimary =
        primary && images.some((i) => i.uuid === primary) ? primary : null;
      const heroPayload = {
        images: images.map((i) => ({
          uuid: i.uuid,
          url: i.url,
          optimized_url: i.optimized_url || null,
        })),
        primary: validPrimary,
        logo: logo
          ? {
              uuid: logo.uuid,
              url: logo.url,
              optimized_url: logo.optimized_url || null,
            }
          : null,
        logo_mode: logoMode,
        logo_scale: logoScale,
        needs_invert: needsInvert,
      };
      updateBlock({
        ...block,
        config: { ...(block.config || {}), hero: heroPayload },
      });
    }, 600);
    return () => clearTimeout(saveRef.current);
  }, [images, primary, logo, logoMode, logoScale, needsInvert]);

  const filledIdx = useMemo(
    () =>
      images.reduce(
        (acc, img, idx) => (img && img.url ? [...acc, idx] : acc),
        []
      ),
    [images]
  );

  const hasUserImages = filledIdx.length > 0;

  useEffect(() => {
    if (filledIdx.length < 2) {
      return () => {};
    }
    timerRef.current = setInterval(() => {
      setActiveIdx((cur) => {
        const pos = filledIdx.indexOf(cur);
        const nextPos = pos === -1 ? 0 : (pos + 1) % filledIdx.length;
        return filledIdx[nextPos];
      });
    }, 7000);
    return () => clearInterval(timerRef.current);
  }, [filledIdx]);

  const go = (n) => {
    const len = images.length;
    const next = ((n % len) + len) % len;
    setActiveIdx(next);
  };

  const onMouseEnter = () => clearInterval(timerRef.current);
  const onMouseLeave = () => {
    if (filledIdx.length < 2) return;
    timerRef.current = setInterval(() => {
      setActiveIdx((cur) => {
        const pos = filledIdx.indexOf(cur);
        const nextPos = pos === -1 ? 0 : (pos + 1) % filledIdx.length;
        return filledIdx[nextPos];
      });
    }, 7000);
  };

  return (
    <HeroSection
      $tall={hasUserImages}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Stage>
        {hasUserImages ? (
          images.map((img, idx) => (
            <Slide key={img.uuid || idx} $active={idx === activeIdx}>
              <SmartImg file={img} alt={`${block.name} — ${idx + 1}`} />
            </Slide>
          ))
        ) : (
          <Slide $active $muted>
            <img src={DEFAULT_HERO} alt={block.name} />
          </Slide>
        )}

        <Shade />

        {images.length > 1 && (
          <Dots>
            {images.map((_, idx) => (
              <Dot
                key={idx}
                $active={idx === activeIdx}
                onClick={() => go(idx)}
                aria-label={tp("block.hero.photo_n", { n: idx + 1 })}
              />
            ))}
          </Dots>
        )}

        {images.length > 1 && (
          <Arrows>
            <Arrow
              onClick={() => go(activeIdx - 1)}
              aria-label={t("block.hero.prev")}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
              >
                <path d="M15 6l-6 6 6 6" />
              </svg>
            </Arrow>
            <Arrow
              onClick={() => go(activeIdx + 1)}
              aria-label={t("block.hero.next")}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
              >
                <path d="M9 6l6 6-6 6" />
              </svg>
            </Arrow>
          </Arrows>
        )}
      </Stage>

      <Identity>
        <Brand
          mode={logoMode}
          logo={logo}
          scale={logoScale}
          invert={needsInvert}
          name={block.name}
        />
        {(block.address || (logo && logoMode !== "text")) && (
          <AddressLine>
            {logo && logoMode !== "text" && (
              <>
                <b>{block.name}</b>
                {block.address && <span className="dot" />}
              </>
            )}
            {block.address}
          </AddressLine>
        )}
      </Identity>

      <HeroControls
        images={images}
        setImages={setImages}
        primary={primary}
        setPrimary={setPrimary}
        logo={logo}
        setLogo={setLogo}
        logoMode={logoMode}
        setLogoMode={setLogoMode}
        logoScale={logoScale}
        setLogoScale={setLogoScale}
        needsInvert={needsInvert}
        setNeedsInvert={setNeedsInvert}
      />
    </HeroSection>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ updateBlock }, dispatch);

export const Hero = connect(null, mapDispatchToProps)(HeroComponent);
